import React from "react";
import { GoDotFill } from "react-icons/go";
import Questions from "components/forms/Questions";
import useDarkMode from "hooks/useDarkMode";

const AboutUs = () => {
  const { theme } = useDarkMode();
  const isDark = theme === "dark";

  const glassStyle = isDark
    ? "bg-[#030014]/60 backdrop-blur-xl border border-cyan-500/20 shadow-[0_0_30px_rgba(102,133,255,0.05)]"
    : "bg-white/70 backdrop-blur-md border border-cyan-100 shadow-[0_8px_32px_rgba(31,38,135,0.07)]";

  const cardGradient = isDark
    ? "linear-gradient(242deg, rgba(237, 241, 255, 0.05) 1.93%, rgba(255, 247, 251, 0.02) 98.08%)"
    : "linear-gradient(242deg, rgba(237, 241, 255, 1) 1.93%, rgba(255, 247, 251, 1) 98.08%)";

  return (
    <div
      className={`flex flex-col gap-[32px] p-[16px] md:p-[40px] rounded-[32px] transition-colors duration-500 ${
        isDark ? "bg-transparent" : "bg-slate-50"
      }`}
    >
      <div className="flex flex-col gap-2">
        <span
          className={`text-[40px] md:text-[56px] font-black tracking-tighter leading-tight ${
            isDark
              ? "text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-200 to-cyan-500 drop-shadow-[0_0_15px_rgba(102,133,255,0.3)]"
              : "text-slate-900"
          }`}
        >
          Մեր Մասին
        </span>
        <div className="h-1.5 w-24 bg-gradient-to-r from-cyan-500 to-emerald-400 rounded-full" />
      </div>

      <div
        className={`flex flex-col gap-[32px] rounded-[32px] p-[24px] md:p-[40px] ${glassStyle}`}
      >
        {/* Hero Banner Area */}
        <div
          style={{
            background: isDark
              ? "linear-gradient(135deg, rgba(6, 27, 100, 0.4) 0%, rgba(3, 0, 20, 0.8) 100%)"
              : "linear-gradient(135deg, #F0F4FF 0%, #FFFFFF 100%)",
            border: isDark
              ? "1px solid rgba(102, 133, 255, 0.1)"
              : "1px solid rgba(102, 133, 255, 0.1)",
          }}
          className="flex flex-col p-[32px] gap-[24px] rounded-[24px] relative overflow-hidden group"
        >
          <div className="absolute top-[-50%] right-[-10%] w-64 h-64 bg-cyan-500/10 rounded-full blur-[80px] group-hover:bg-cyan-500/20 transition-all duration-700" />

          <span
            className={`text-[28px] md:text-[34px] font-bold leading-tight ${isDark ? "text-cyan-100" : "text-slate-800"}`}
          >
            Ապագան արդեն այստեղ է, բայց արդյո՞ք այն կանխատեսելի է։
          </span>
          <span
            className={`text-[18px] md:text-[20px] font-[400] leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}
          >
            Prognos-ը միակ հարթակն է, որտեղ կանխատեսումները ստուգվում են
            իրականության դեմ, իսկ ճշգրիտ կանխատեսողները վաստակում են իրենց
            վարկանիշը ոչ թե խոսքերով, այլ փաստերով։ Մենք ստեղծել ենք խելացի
            գնահատման համակարգ, որը թույլ է տալիս հասարակությանը որոշել, թե ով է
            իրականում հասկանում իրադարձությունների զարգացումը, իսկ ով պարզապես
            ենթադրություններ է անում։
          </span>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[20px]">
          {[
            {
              title: "Կանխատեսումները Այլևս Խոսքեր Չեն",
              desc: "Մենք չափում ենք դրանց ճշգրտությունը ոչ թե անձնական կարծիքով, այլ իրական արդյունքներով։",
              iconColor: "text-cyan-400",
            },
            {
              title: "Անկախ և Օբյեկտիվ Համակարգ",
              desc: "Վարկանիշներն ու գնահատականները ամբողջությամբ հիմնված են փաստերի վրա, այլ ոչ թե ազդեցության կամ իշխանության։",
              iconColor: "text-emerald-400",
            },
            {
              title: "Հանրության Ձայնը Հաշվի է Առնվում",
              desc: "Օգտատերերը կարող են քվեարկել, բայց նրանք չեն կարող կեղծել արդյունքները։ Prognos-ը ֆիլտրում է մանիպուլյացիաները։",
              iconColor: "text-violet-400",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              style={{ background: cardGradient }}
              className={`flex flex-col p-[28px] gap-[20px] rounded-[24px] border transition-all duration-300 hover:-translate-y-2 ${
                isDark
                  ? "border-white/5 hover:border-cyan-500/30"
                  : "border-slate-200/50 hover:border-cyan-300"
              }`}
            >
              <span
                className={`text-[22px] font-bold leading-tight ${isDark ? "text-white" : "text-slate-800"}`}
              >
                {item.title}
              </span>
              <span
                className={`text-[16px] leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}
              >
                {item.desc}
              </span>
            </div>
          ))}
        </div>

        {/* Steps Section */}
        <div
          className={`flex flex-col p-[32px] gap-[32px] rounded-[24px] border ${
            isDark
              ? "bg-white/5 border-white/10"
              : "bg-slate-50 border-slate-200"
          }`}
        >
          <span
            className={`text-[28px] font-bold ${isDark ? "text-white" : "text-slate-800"}`}
          >
            Ինչպես է Աշխատում Prognos-ը
          </span>
          <div className="flex flex-col gap-[20px] relative">
            {/* Vertical Connector Line for large screens */}
            <div className="absolute left-[7px] top-4 bottom-4 w-[1px] bg-gradient-to-b from-cyan-500/50 via-emerald-500/50 to-cyan-500/50 hidden md:block" />

            {[
              {
                label: "Կանխատեսումը:",
                text: "Ինչ-որ մեկը կանխատեսում է իրադարձության արդյունքը։",
              },
              {
                label: "Իրադարձությունը տեղի է ունենում:",
                text: "Մենք սպասում ենք արդյունքին։",
              },
              {
                label: "Ստուգում + Հաշվարկ:",
                text: "Համակարգը ստուգում է կանխատեսման ճշգրտությունը։",
              },
              {
                label: "Վարկանիշի Թարմացում:",
                text: "Ճշգրիտ կանխատեսումները բարձրացնում են հեղինակի վարկանիշը։",
              },
              {
                label: "Հանրության Մասնակցություն:",
                text: "Օգտատերերը գնահատում են կանխատեսումների տրամաբանությունը։",
              },
            ].map((step, idx) => (
              <div key={idx} className="flex items-start gap-[16px] group">
                <div className="mt-1.5 z-10 transition-transform duration-300 group-hover:scale-125">
                  <GoDotFill
                    className={
                      isDark
                        ? "text-cyan-400 drop-shadow-[0_0_8px_rgba(102,133,255,0.8)]"
                        : "text-cyan-600"
                    }
                    size={16}
                  />
                </div>
                <div className="flex flex-col md:flex-row md:items-center gap-[4px] md:gap-[8px]">
                  <span
                    className={`font-bold text-[18px] ${isDark ? "text-white" : "text-slate-800"}`}
                  >
                    {step.label}
                  </span>
                  <span
                    className={`text-[18px] font-[400] ${isDark ? "text-slate-400" : "text-slate-600"}`}
                  >
                    {step.text}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Vision Section */}
        <div
          className={`flex flex-col p-[32px] gap-[24px] rounded-[24px] relative overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-cyan-900/20 to-emerald-900/10"
              : "bg-gradient-to-br from-blue-50 to-indigo-50"
          }`}
        >
          <div className="flex items-center gap-4">
            <span
              className={`text-[28px] font-bold ${isDark ? "text-white" : "text-slate-800"}`}
            >
              Մեր Տեսլականը
            </span>
            <div className="flex-grow h-[1px] bg-gradient-to-r from-cyan-500/50 to-transparent" />
          </div>

          <div
            className={`p-[32px] rounded-[24px] border ${
              isDark ? "bg-black/20 border-white/5" : "bg-white/60 border-white"
            }`}
          >
            <span
              className={`text-[18px] md:text-[20px] font-medium leading-relaxed block mb-6 ${isDark ? "text-slate-200" : "text-slate-700"}`}
            >
              Prognos-ը միակ հարթակն է, որտեղ կանխատեսումները ստուգվում են
              իրականության դեմ, իսկ ճշգրիտ կանխատեսողները վաստակում են իրենց
              վարկանիշը ոչ թե խոսքերով, այլ փաստերով։ Մենք ստեղծել ենք խելացի
              գնահատման համակարգ, որը թույլ է տալիս հասարակությանը որոշել, թե ով
              է իրականում հասկանում իրադարձությունների զարգացումը։
            </span>
            <p
              className={`text-[22px] md:text-[26px] font-black italic text-center tracking-tight ${
                isDark
                  ? "text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400"
                  : "text-cyan-700"
              }`}
            >
              Միացե՛ք Prognos-ին և բացահայտե՛ք ճշգրտության իրական ուժը։
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center pt-8">
        <Questions />
      </div>
    </div>
  );
};

export default AboutUs;
